import { Facility } from '@reliance/types/models/Facility';
import React, { useState } from 'react';
import api from '../common/api';
import InmatePicker from '../inmate/Picker';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

type AddTrustAccountTransactionProps = {
  facility: Facility;
  onTransactionAdded: () => void;
};

type TransactionFormData = {
  inmateId: number | null;
  amount: string;
  description: string;
  transactionType: 'credit' | 'debit';
};

const AddTrustAccountTransaction: React.FC<AddTrustAccountTransactionProps> = ({
  facility,
  onTransactionAdded
}) => {
  const [formData, setFormData] = useState<TransactionFormData>({
    inmateId: null,
    amount: '',
    description: '',
    transactionType: 'credit'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (field: keyof TransactionFormData, value: string | number | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.inmateId || !formData.amount || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (formData.transactionType === 'debit') {
        await api.post('/inmate-account/commissary-transfer/debit', {
          inmateId: formData.inmateId,
          amount: amount,
          description: formData.description,
          enteredBy: 'Staff Manual Entry'
        });
      } else {
        await api.post('/inmate-account/commissary-transfer', {
          inmateId: formData.inmateId,
          amount: amount,
          description: formData.description,
          enteredBy: 'Staff Manual Entry'
        });
      }

      setSuccess('Transaction added successfully');
      setFormData({
        inmateId: null,
        amount: '',
        description: '',
        transactionType: 'credit'
      });

      onTransactionAdded();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        Add Transaction
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Transaction Type</Form.Label>
                <Form.Select
                  value={formData.transactionType}
                  onChange={(e) => handleInputChange('transactionType', e.target.value as 'credit' | 'debit')}>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={9}>
              <InmatePicker
                onInmateSelected={(id) => handleInputChange('inmateId', id)}
                inmateId={formData.inmateId}
                showReleased={false}
                placeholder="Select an inmate"
                facilityId={facility.id} />
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="Enter amount"
                  required />
              </Form.Group>
            </Col>
            <Col md={9}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter description"
                  required />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              variant="primary"
              disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spin fa-spinner me-2"></i>
                  Adding Transaction...
                </>
              ) : (
                'Add Transaction'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddTrustAccountTransaction;